/**
 * Map Status Indicator - Shows data freshness and provides manual refresh
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import "./MapStatusIndicator.css";

export interface MapStatusIndicatorProps {
    lastUpdated?: string; // ISO 8601 timestamp
    isRefetching?: boolean;
    onRefresh?: () => void;
}

export const MapStatusIndicator = ({
    lastUpdated,
    isRefetching = false,
    onRefresh
}: MapStatusIndicatorProps): JSX.Element => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const timeAgo = useMemo(() => {
        if (!lastUpdated) {
            return null;
        }

        const now = new Date();
        const updated = new Date(lastUpdated);
        const diffMs = now.getTime() - updated.getTime();
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) {
            return t("map.status.daysAgo", { count: diffDays });
        }
        if (diffHours > 0) {
            return t("map.status.hoursAgo", { count: diffHours });
        }
        if (diffMinutes > 0) {
            return t("map.status.minutesAgo", { count: diffMinutes });
        }
        return t("map.status.justNow");
    }, [lastUpdated, t]);

    const handleRefresh = () => {
        if (onRefresh) {
            onRefresh();
        } else {
            // Default: invalidate all map queries
            queryClient.invalidateQueries({ queryKey: ["map"] });
        }
    };

    const isStale = useMemo(() => {
        if (!lastUpdated) {
            return false;
        }
        const updated = new Date(lastUpdated);
        const diffMs = Date.now() - updated.getTime();
        return diffMs > 5 * 60 * 1000; // 5 minutes
    }, [lastUpdated]);

    return (
        <div className="map-status-indicator" role="status" aria-live="polite">
            {lastUpdated && (
                <div className="map-status-indicator__time">
                    <span
                        className={`map-status-indicator__dot ${isStale ? "map-status-indicator__dot--stale" : ""
                            }`}
                        aria-hidden="true"
                    />
                    <span className="map-status-indicator__text">
                        {t("map.status.lastUpdated")} {timeAgo}
                    </span>
                </div>
            )}

            <button
                type="button"
                className="map-status-indicator__refresh"
                onClick={handleRefresh}
                disabled={isRefetching}
                aria-label={t("map.status.refresh")}
                title={t("map.status.refresh")}
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={isRefetching ? "map-status-indicator__icon--spinning" : ""}
                >
                    <path
                        d="M8 2V6M8 2L6 4M8 2L10 4M14 8C14 11.3137 11.3137 14 8 14M14 8C14 4.68629 11.3137 2 8 2M14 8H10M2 8C2 11.3137 4.68629 14 8 14M2 8C2 4.68629 4.68629 2 8 2M2 8H6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                {isRefetching && (
                    <span className="map-status-indicator__refreshing">
                        {t("map.status.refreshing")}
                    </span>
                )}
            </button>
        </div>
    );
};